import * as styledElements from "./OrderBoardStyles";
import FileInput from "../common/FileInput";
import GerberFileIdentifier from "./GerberFileIdentifier";
import Button from "../common/Button";
import { ButtonTypes } from "../common/Button/constants";
import React, { useEffect } from "react";
import { useFormikContext } from "formik";
import {
  BOMFileInfo,
  GerberFileLayerMapping,
  PNPFileInfo,
  CADSources,
} from "../../reducers/uploadedBoard";
import { SelectContainer, SelectOption } from "../common/FancySelect";
import { FONT_SIZE } from "../../constants/Styles";
import { getInitialValues } from "./index";
import { useCookies } from "react-cookie";
import { LAST_CAD_SOURCE_UPLOADED } from "../../reducers/models/flags";

export interface UploadedFile {
  extension: string;
  multiple?: boolean;
}

interface CADSourceDetails {
  cadSource: CADSources;
  requiredFiles: UploadedFile[];
  optionalFiles?: UploadedFile[];
  displayText: string;
}

export const UPLOAD_MODES_DATA: { [id: string]: CADSourceDetails } = {
  [CADSources.KICAD]: {
    cadSource: CADSources.KICAD,
    requiredFiles: [{ extension: ".kicad_pcb" }],
    optionalFiles: [{ extension: ".kicad_sch", multiple: true }],
    displayText: "KiCad",
  },
  [CADSources.GERBER]: {
    cadSource: CADSources.GERBER,
    requiredFiles: [{ extension: ".zip" }],
    displayText: "Gerber",
  },
};

export interface UploadFormValues {
  cadSource: CADSources;
  files: File[][];
  gerberFileLayerMapping: null | GerberFileLayerMapping;
  bomFileInfo: null | BOMFileInfo;
  pnpFileInfo: null | PNPFileInfo;
  assemblyDrawing: null | string[];
  fabDrawing: null | string[];
  referenceFile: null | string[];
}

export default function UploadForm({ loading }: { loading: boolean }) {
  const setCookie = useCookies()[1];
  const { handleSubmit, setValues, values, isValid, setFieldValue } =
    useFormikContext<UploadFormValues>();
  useEffect(() => {
    void setValues(getInitialValues(values.cadSource));
  }, [values.cadSource, setValues]);

  useEffect(() => {
    const requiredFilesCount =
      UPLOAD_MODES_DATA[values.cadSource]!.requiredFiles.length;
    const optionalFilesCount =
      UPLOAD_MODES_DATA[values.cadSource]!.optionalFiles?.length || 0;

    const newFiles = Array(requiredFilesCount + optionalFilesCount).fill(
      [],
    ) as File[][];

    if (values.files.length !== newFiles.length) {
      void setFieldValue("files", newFiles);
    }
  }, [setFieldValue, values.files, values.cadSource]);

  return (
    <styledElements.Form onSubmit={handleSubmit}>
      <styledElements.SelectProgram>
        <styledElements.PickerText>Project type:</styledElements.PickerText>
        <SelectContainer>
          {Object.values(CADSources)
            .sort()
            .map((cadSource) => (
              <SelectOption
                key={cadSource}
                fontSize={FONT_SIZE.size18}
                onClick={() => {
                  void setFieldValue("cadSource", cadSource);
                  setCookie(LAST_CAD_SOURCE_UPLOADED, cadSource);
                }}
                isSelected={values.cadSource == cadSource}
              >
                {cadSource}
              </SelectOption>
            ))}{" "}
        </SelectContainer>
      </styledElements.SelectProgram>
      <styledElements.Container>
        {UPLOAD_MODES_DATA[values.cadSource]!.requiredFiles.map(
          (requiredFile: UploadedFile, requiredFileIndex) => {
            if (requiredFileIndex > values.files.length - 1) {
              // There could be a mismatch due to the asynchronous nature of react effects
              return null;
            }
            return (
              <FileInput
                disabled={loading}
                key={requiredFile.extension}
                fileExtensions={[requiredFile.extension]}
                files={values.files[requiredFileIndex]!}
                setFiles={(newFiles: File[]) => {
                  void setValues({
                    ...values,
                    files: values.files.map((existingFiles, fileIndex) =>
                      fileIndex == requiredFileIndex ? newFiles : existingFiles,
                    ),
                  });
                }}
                isMultiple={requiredFile.multiple}
              />
            );
          },
        )}
        {UPLOAD_MODES_DATA[values.cadSource]!.optionalFiles?.map(
          (optionalFile: UploadedFile, optionalFileIndex) => {
            const index =
              UPLOAD_MODES_DATA[values.cadSource]!.requiredFiles.length +
              optionalFileIndex;
            if (!values.files[index]) {
              // There could be a mismatch due to the asynchronous nature of react effects
              return null;
            }
            return (
              <FileInput
                disabled={loading}
                key={optionalFile.extension}
                fileExtensions={[optionalFile.extension]}
                files={values.files[index]}
                setFiles={(newFiles: File[]) => {
                  void setValues({
                    ...values,
                    files: values.files.map((existingFiles, fileIndex) =>
                      fileIndex == index ? newFiles : existingFiles,
                    ),
                  });
                }}
                isMultiple={optionalFile.multiple}
                optional
              />
            );
          },
        )}
      </styledElements.Container>
      <styledElements.DocsLink
        href={"/docs/uploading-your-board#required-files"}
        target="_blank"
      >
        See our docs for an explanation of the files we accept.
      </styledElements.DocsLink>
      {values.cadSource == CADSources.GERBER && (
        <GerberFileIdentifier file={values.files[0]?.[0]} />
      )}
      <Button
        type={ButtonTypes.SUBMIT}
        label={loading ? "Uploading Files" : "Submit"}
        disabled={!isValid || loading}
        {...(!isValid && !loading
          ? {
              leftIconProps: {
                src: "/icons/locked.svg",
                alt: "locked",
                iconDimension: 24,
              },
            }
          : {})}
        centerText
      />
    </styledElements.Form>
  );
}
